import { useRef, useCallback, useState } from 'react'
import {
    ChatMessage,
    ConnectionStatus,
    StreamingMeta,
    StreamingProgress,
    StreamingEstimate,
    StreamingError,
    ArtifactData,
    ToolExecution,
    WaitingFormData,
    OutputCardData,
    ExtractedEntities,
} from '../types'
import { csrfHeaders } from '../utils/csrf'

interface UseAgentChatProps {
    agentName: string
    /** Called when streaming progress updates (for park chat) */
    onProgressUpdate?: (progress: StreamingProgress | null) => void
}

interface SendAgentMessageOptions {
    displayMessage?: string
    images?: string[]
    imagePreviews?: string[]
}

interface LastRequest {
    userText: string
    displayMessage?: string
    images?: string[]
    imagePreviews?: string[]
}

interface UseAgentChatReturn {
    messages: ChatMessage[]
    connectionStatus: ConnectionStatus
    isLoading: boolean
    configError: string | null
    conversationId: string
    currentRunId: string | null
    currentProgress: StreamingProgress | null
    sendMessage: (message: string, options?: SendAgentMessageOptions) => void
    cancelRequest: () => void
    newChat: () => void
    restoreConversation: (conversationId: string, messages: ChatMessage[]) => void
    resumeWaitingForm: (messageId: string, formValues: Record<string, unknown>) => void
    resumeAfterElicitation: (messageId: string) => void
    cancelWaitingForm: (messageId: string) => void
    retryTool: (agentMessageId: string, tool: ToolExecution) => void
}

const generateId = (): string =>
    `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const generateConversationId = (): string =>
    `conv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const getClientToken = (): string => {
    try {
        return crypto.randomUUID()
    } catch {
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`
    }
}

const defaultProgress = (model = 'unknown'): StreamingProgress => ({
    status: 'thinking',
    elapsed_ms: 0,
    iteration: 1,
    max_iterations: 15,
    tools_completed: [],
    tools_pending: [],
    model,
    reasoning_effort: 'medium',
})

function parseWaitingForm(frame: Record<string, unknown>): WaitingFormData {
    const frameMode: 'form' | 'url' = frame.mode === 'url' ? 'url' : 'form'
    const resume = frame.resume as { endpoint?: string; token?: string } | undefined
    return {
        task_id: resume?.token || '',
        fields: Array.isArray(frame.fields)
            ? (frame.fields as Record<string, unknown>[]).map(f => ({
                  name: String(f.name ?? ''),
                  type: String(f.type || 'string'),
                  description: String(f.description || f.title || f.name || ''),
                  required: f.required !== false,
                  default_value: f.default_value,
                  suggestions: f.suggestions as string[] | undefined,
                  dynamic_suggest: f.dynamic_suggest as WaitingFormData['fields'][0]['dynamic_suggest'],
                  validation: f.validation as WaitingFormData['fields'][0]['validation'],
                  current_value: f.current_value,
                  format: f.format as string | undefined,
              }))
            : [],
        tool_name: frame.tool_name as string | undefined,
        original_request: frame.prompt as string | undefined,
        resume_endpoint: resume?.endpoint,
        resume_token: resume?.token,
        mode: frameMode,
        elicitations: Array.isArray(frame.elicitations)
            ? (frame.elicitations as WaitingFormData['elicitations'])
            : undefined,
        prompt: frame.prompt as string | undefined,
        confirmation:
            frame.confirmation && typeof frame.confirmation === 'object'
                ? (frame.confirmation as WaitingFormData['confirmation'])
                : undefined,
    }
}

export const useAgentChat = ({
    agentName,
    onProgressUpdate,
}: UseAgentChatProps): UseAgentChatReturn => {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connected')
    const [isLoading, setIsLoading] = useState(false)
    const [conversationId, setConversationId] = useState(generateConversationId)
    const [currentRunId, setCurrentRunId] = useState<string | null>(null)
    const [currentProgress, setCurrentProgress] = useState<StreamingProgress | null>(null)

    const abortControllerRef = useRef<AbortController | null>(null)
    const conversationIdRef = useRef<string>(conversationId)
    conversationIdRef.current = conversationId
    const currentAgentMsgIdRef = useRef<string | null>(null)
    const isLoadingRef = useRef(false)
    const msgStartTimeRef = useRef<number>(0)
    const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const toolCallNamesRef = useRef<Record<string, string>>({})
    const lastRequestRef = useRef<LastRequest | null>(null)
    const rateLimitAutoRetryCountRef = useRef(0)
    const sendMessageRef = useRef<(text: string, opts?: SendAgentMessageOptions) => void>(() => {})
    const messagesRef = useRef<ChatMessage[]>([])
    messagesRef.current = messages

    const updateProgress = useCallback(
        (progress: StreamingProgress | null) => {
            setCurrentProgress(progress)
            onProgressUpdate?.(progress)
        },
        [onProgressUpdate],
    )

    const stopElapsedInterval = useCallback(() => {
        if (elapsedIntervalRef.current !== null) {
            clearInterval(elapsedIntervalRef.current)
            elapsedIntervalRef.current = null
        }
    }, [])

    const finishStream = useCallback(
        (agentMsgId: string, keepLoading = false) => {
            stopElapsedInterval()
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === agentMsgId
                        ? {
                              ...msg,
                              isStreaming: false,
                              elapsedMs: Date.now() - msgStartTimeRef.current,
                          }
                        : msg,
                ),
            )
            if (!keepLoading) {
                isLoadingRef.current = false
                setIsLoading(false)
                setConnectionStatus('connected')
                currentAgentMsgIdRef.current = null
                updateProgress(null)
            }
        },
        [stopElapsedInterval, updateProgress],
    )

    const startElapsedInterval = useCallback((agentMsgId: string) => {
        elapsedIntervalRef.current = setInterval(() => {
            const id = currentAgentMsgIdRef.current
            if (!id) return
            const elapsed = Date.now() - msgStartTimeRef.current
            setMessages(prev =>
                prev.map(msg => (msg.id === id ? { ...msg, elapsedMs: elapsed } : msg)),
            )
        }, 200)
    }, [])

    const processSseLine = useCallback(
        (line: string, agentMsgId: string, onRateLimitRetry?: () => void) => {
            if (!line.trim() || line.startsWith(':') || line.startsWith('retry:')) return

            const jsonStr = line.startsWith('data:') ? line.slice(5).trim() : line.trim()
            if (!jsonStr.startsWith('{')) return

            let frame: Record<string, unknown>
            try {
                frame = JSON.parse(jsonStr)
            } catch {
                return
            }

            switch (frame.type) {
                case 'meta': {
                    const meta: StreamingMeta = {
                        agentId: frame.agent_id as string | undefined,
                        agentName: frame.agent_name as string | undefined,
                        model: frame.model as string | undefined,
                        toolNames: frame.tool_names as string[] | undefined,
                        toolCount:
                            (frame.tool_count as number | undefined) ??
                            (frame.tool_names as string[] | undefined)?.length,
                        tier: frame.tier as string | undefined,
                        reasoningEffort: frame.reasoning_effort as string | undefined,
                    }
                    const runId = frame.run_id as string | undefined
                    if (runId) setCurrentRunId(runId)
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? { ...msg, streamingMeta: meta, runId: runId || msg.runId }
                                : msg,
                        ),
                    )
                    break
                }
                case 'estimate': {
                    const estimate: StreamingEstimate = {
                        estimatedMs: frame.estimated_ms as number | undefined,
                        confidence: (frame.confidence as string) || 'medium',
                        message: frame.message as string | undefined,
                    }
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId ? { ...msg, streamingEstimate: estimate } : msg,
                        ),
                    )
                    break
                }
                case 'progress':
                case 'heartbeat': {
                    const progressPayload =
                        frame.type === 'heartbeat'
                            ? (frame.progress as StreamingProgress)
                            : (frame as unknown as StreamingProgress)
                    if (!progressPayload) break
                    updateProgress({
                        ...(currentProgress ?? {}),
                        ...progressPayload,
                    })
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? {
                                      ...msg,
                                      streamingProgress: {
                                          ...(msg.streamingProgress ?? {}),
                                          ...progressPayload,
                                      },
                                  }
                                : msg,
                        ),
                    )
                    break
                }
                case 'chunk': {
                    const data = typeof frame.data === 'string' ? frame.data : ''
                    if (!data) return
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? { ...msg, content: (msg.content || '') + data }
                                : msg,
                        ),
                    )
                    break
                }
                case 'tool': {
                    if (!frame.call_id) return
                    const callId = frame.call_id as string
                    const toolName = (frame.name as string) || 'unknown'
                    let parsedArgs: Record<string, unknown> = {}
                    if (typeof frame.arguments === 'string') {
                        try {
                            parsedArgs = JSON.parse(frame.arguments) as Record<string, unknown>
                        } catch {
                            parsedArgs = { raw: frame.arguments }
                        }
                    } else {
                        parsedArgs = (frame.arguments as Record<string, unknown>) || {}
                    }
                    toolCallNamesRef.current[callId] = toolName
                    const newExecution: ToolExecution = {
                        call_id: callId,
                        name: toolName,
                        arguments: parsedArgs,
                        status: 'executing',
                        timestamp: Date.now(),
                        tool_selection_reason: frame.tool_selection_reason as string | undefined,
                    }
                    setMessages(prev =>
                        prev.map(msg => {
                            if (msg.id !== agentMsgId) return msg
                            const existing = msg.toolExecutions ?? []
                            return {
                                ...msg,
                                toolExecutions: [
                                    ...existing.filter(t => t.call_id !== callId),
                                    newExecution,
                                ],
                            }
                        }),
                    )
                    break
                }
                case 'tool_result': {
                    if (!frame.call_id) return
                    const callId = frame.call_id as string
                    const resultPayload = frame.result as ToolExecution['result'] | undefined
                    const succeeded = resultPayload?.success ?? true
                    const endTs = Date.now()
                    setMessages(prev =>
                        prev.map(msg => {
                            if (msg.id !== agentMsgId) return msg
                            return {
                                ...msg,
                                toolExecutions: (msg.toolExecutions ?? []).map(t =>
                                    t.call_id === callId
                                        ? {
                                              ...t,
                                              status: succeeded ? 'completed' : 'failed',
                                              endTimestamp: endTs,
                                              result: resultPayload as ToolExecution['result'],
                                              error: succeeded
                                                  ? undefined
                                                  : (frame.error as string) || 'Tool failed',
                                          }
                                        : t,
                                ),
                            }
                        }),
                    )
                    if (succeeded) {
                        const resolvedToolName = toolCallNamesRef.current[callId]
                        if (resolvedToolName) {
                            window.dispatchEvent(
                                new CustomEvent('eid:agent:effect', {
                                    detail: resolvedToolName,
                                }),
                            )
                        }
                    }
                    break
                }
                case 'artifact': {
                    const targetId = (frame.message_id as string) || agentMsgId
                    const artifact: ArtifactData = {
                        artifact_id: frame.artifact_id as string,
                        message_id: frame.message_id as string,
                        tool_call_id: frame.tool_call_id as string | undefined,
                        filename: frame.filename as string,
                        mime_type: frame.mime_type as string,
                        download_url: frame.download_url as string,
                        size_bytes: (frame.size_bytes as number) || 0,
                        checksum_sha256: frame.checksum_sha256 as string | undefined,
                        expires_at: frame.expires_at as string,
                        classification: frame.classification as string | undefined,
                        display_inline: frame.display_inline as boolean | undefined,
                        inline_preview_url: frame.inline_preview_url as string | undefined,
                        description: frame.description as string | undefined,
                    }
                    setMessages(prev =>
                        prev.map(msg => {
                            if (msg.id !== targetId) return msg
                            const existing = msg.artifacts ?? []
                            if (existing.some(a => a.artifact_id === artifact.artifact_id)) {
                                return msg
                            }
                            return { ...msg, artifacts: [...existing, artifact] }
                        }),
                    )
                    break
                }
                case 'awaiting_input': {
                    const waitingFormData = parseWaitingForm(frame)
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? {
                                      ...msg,
                                      messageType: 'waiting_form',
                                      waitingForm: waitingFormData,
                                      content: '',
                                      isStreaming: false,
                                      isAwaitingInput: true,
                                  }
                                : msg,
                        ),
                    )
                    finishStream(agentMsgId)
                    break
                }
                case 'output_card': {
                    const card = frame.card as OutputCardData | undefined
                    if (!card) return
                    const cardMsg: ChatMessage = {
                        id: `output-card-${card.card_id || Date.now()}`,
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                        messageType: 'output_card',
                        outputCard: card,
                    }
                    setMessages(prev => [...prev, cardMsg])
                    break
                }
                case 'rationale': {
                    const tier = frame.agent_tier as string | undefined
                    const content = (frame.content || frame.delta) as string | undefined
                    if (tier && tier !== 'L0' && content) {
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === agentMsgId
                                    ? { ...msg, rationale: String(content), agentTier: tier }
                                    : msg,
                            ),
                        )
                    }
                    break
                }
                case 'extraction': {
                    const entities = (frame.entities || {}) as Record<string, unknown>
                    const extractedEntities: ExtractedEntities = {
                        emails: Array.isArray(entities.emails)
                            ? (entities.emails as string[])
                            : undefined,
                        inferredNames: Array.isArray(entities.inferred_names)
                            ? (entities.inferred_names as ExtractedEntities['inferredNames'])
                            : Array.isArray(entities.inferredNames)
                              ? (entities.inferredNames as ExtractedEntities['inferredNames'])
                              : undefined,
                        dates: Array.isArray(entities.dates)
                            ? (entities.dates as string[])
                            : undefined,
                        identifiers: Array.isArray(entities.identifiers)
                            ? (entities.identifiers as string[])
                            : undefined,
                        urls: Array.isArray(entities.urls)
                            ? (entities.urls as string[])
                            : undefined,
                        message:
                            typeof frame.message === 'string' ? frame.message : undefined,
                    }
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? { ...msg, extractedEntities }
                                : msg,
                        ),
                    )
                    break
                }
                case 'final': {
                    const reasonToStatus: Record<string, string> = {
                        done: 'complete',
                        max_iterations: 'max_iterations',
                        cancelled: 'cancelled',
                        error: 'error',
                        timeout: 'timeout',
                    }
                    const finalStatus =
                        reasonToStatus[String(frame.reason || 'done')] || 'complete'

                    setMessages(prev =>
                        prev.map(msg => {
                            if (msg.id !== agentMsgId) return msg
                            const existingProgress = msg.streamingProgress || {}
                            const finalElapsedMs =
                                typeof frame.elapsed_ms === 'number'
                                    ? frame.elapsed_ms
                                    : existingProgress.elapsed_ms
                            const nextProgress: StreamingProgress = {
                                ...existingProgress,
                                status: finalStatus,
                                elapsed_ms:
                                    typeof finalElapsedMs === 'number' ? finalElapsedMs : 0,
                                current_tool: null,
                                tools_pending: [],
                                estimated_remaining_ms: 0,
                            }
                            updateProgress(nextProgress)
                            const content =
                                !msg.content && typeof frame.text === 'string'
                                    ? frame.text
                                    : msg.content || ''
                            const update: ChatMessage = {
                                ...msg,
                                content,
                                isStreaming: false,
                                runId: (frame.run_id as string) || msg.runId,
                                streamingProgress: nextProgress,
                            }
                            if (frame.reason === 'timeout') {
                                update.timeoutInfo = {
                                    message:
                                        (frame.message as string) ||
                                        'The request timed out.',
                                    suggestions: Array.isArray(frame.suggestions)
                                        ? (frame.suggestions as string[])
                                        : [],
                                    canRetry: !!frame.can_retry,
                                    partialResults: Array.isArray(frame.partial_results)
                                        ? (frame.partial_results as unknown[])
                                        : [],
                                }
                            }
                            return update
                        }),
                    )
                    if (frame.run_id) setCurrentRunId(frame.run_id as string)
                    finishStream(agentMsgId)
                    break
                }
                case 'error': {
                    const streamingError: StreamingError = {
                        type: (frame.error_type as string) || 'internal',
                        message: (frame.message as string) || 'Agent returned an error.',
                        code: (frame.code as string) || 'unknown',
                        recoverable: frame.recoverable !== false,
                        retryAfterMs: frame.retry_after_ms as number | undefined,
                    }
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === agentMsgId
                                ? {
                                      ...msg,
                                      streamingError,
                                      content:
                                          msg.content || `⚠️ ${streamingError.message}`,
                                      isStreaming: false,
                                  }
                                : msg,
                        ),
                    )
                    finishStream(agentMsgId)

                    if (
                        streamingError.recoverable &&
                        streamingError.retryAfterMs &&
                        streamingError.type === 'rate_limit' &&
                        rateLimitAutoRetryCountRef.current < 1 &&
                        onRateLimitRetry
                    ) {
                        rateLimitAutoRetryCountRef.current += 1
                        const retryInMs = Math.max(250, Number(streamingError.retryAfterMs))
                        setTimeout(() => {
                            if (isLoadingRef.current) return
                            const req = lastRequestRef.current
                            if (!req?.userText?.trim() && !req?.images?.length) return
                            onRateLimitRetry()
                        }, retryInMs)
                    }
                    break
                }
            }
        },
        [currentProgress, finishStream, updateProgress],
    )

    const readSseStream = useCallback(
        async (response: Response, agentMsgId: string, onRateLimitRetry?: () => void) => {
            const hdrRunId = response.headers.get('x-agent-run-id')
            if (hdrRunId) setCurrentRunId(hdrRunId)

            const reader = response.body?.getReader()
            if (!reader) throw new Error('Response body is not readable')

            const decoder = new TextDecoder()
            let buffer = ''
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() ?? ''
                for (const line of lines) processSseLine(line, agentMsgId, onRateLimitRetry)
            }
            if (buffer.trim()) processSseLine(buffer, agentMsgId, onRateLimitRetry)
        },
        [processSseLine],
    )

    const beginAgentMessage = useCallback((): string => {
        const agentMsgId = generateId()
        const progress = defaultProgress(agentName)
        const agentMsg: ChatMessage = {
            id: agentMsgId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isStreaming: true,
            toolCalls: [],
            toolExecutions: [],
            streamingProgress: progress,
            elapsedMs: 0,
        }
        setMessages(prev => [...prev, agentMsg])
        currentAgentMsgIdRef.current = agentMsgId
        isLoadingRef.current = true
        msgStartTimeRef.current = Date.now()
        setIsLoading(true)
        setConnectionStatus('connecting')
        updateProgress(progress)
        startElapsedInterval(agentMsgId)
        return agentMsgId
    }, [agentName, startElapsedInterval, updateProgress])

    const runExecuteStream = useCallback(
        async (
            body: Record<string, unknown>,
            agentMsgId: string,
            onRateLimitRetry?: () => void,
        ) => {
            const controller = new AbortController()
            abortControllerRef.current = controller
            try {
                const response = await fetch(
                    `/api/crud/agents/${encodeURIComponent(agentName)}/execute`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
                        credentials: 'include',
                        signal: controller.signal,
                        body: JSON.stringify(body),
                    },
                )
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                }
                await readSseStream(response, agentMsgId, onRateLimitRetry)
                if (currentAgentMsgIdRef.current === agentMsgId) {
                    finishStream(agentMsgId)
                }
            } catch (err: unknown) {
                if (err instanceof Error && err.name === 'AbortError') return
                stopElapsedInterval()
                setMessages(prev =>
                    prev.map(msg =>
                        msg.id === agentMsgId
                            ? {
                                  ...msg,
                                  content: '⚠️ Could not reach the agent. Please try again.',
                                  isStreaming: false,
                              }
                            : msg,
                    ),
                )
                isLoadingRef.current = false
                setIsLoading(false)
                setConnectionStatus('error')
                currentAgentMsgIdRef.current = null
                updateProgress(null)
            }
        },
        [agentName, readSseStream, finishStream, stopElapsedInterval, updateProgress],
    )

    const sendMessage = useCallback(
        (userText: string, options?: SendAgentMessageOptions) => {
            const imageDataUrls = options?.images ?? []
            const hasImages = imageDataUrls.length > 0
            if ((!userText.trim() && !hasImages) || isLoadingRef.current) return

            lastRequestRef.current = {
                userText,
                displayMessage: options?.displayMessage,
                images: imageDataUrls.length ? imageDataUrls : undefined,
                imagePreviews: options?.imagePreviews,
            }

            const userMsg: ChatMessage = {
                id: generateId(),
                role: 'user',
                content: options?.displayMessage ?? userText,
                timestamp: new Date(),
                images: options?.imagePreviews?.length
                    ? options.imagePreviews
                    : hasImages
                      ? imageDataUrls
                      : undefined,
            }

            setMessages(prev => [...prev, userMsg])
            const agentMsgId = beginAgentMessage()

            const retrySelf = () => {
                const req = lastRequestRef.current
                if (!req) return
                sendMessageRef.current(req.userText, {
                    displayMessage: req.displayMessage,
                    images: req.images,
                    imagePreviews: req.imagePreviews,
                })
            }

            void runExecuteStream(
                {
                    prompt: userText,
                    conversation_id: conversationIdRef.current,
                    client_run_token: getClientToken(),
                    ...(hasImages ? { images: imageDataUrls } : {}),
                },
                agentMsgId,
                retrySelf,
            )
        },
        [beginAgentMessage, runExecuteStream],
    )

    sendMessageRef.current = sendMessage

    const resumeWaitingForm = useCallback(
        (messageId: string, formValues: Record<string, unknown>) => {
            const waitingMsg = messagesRef.current.find(m => m.id === messageId)
            const resumeData = waitingMsg?.waitingForm
            if (!resumeData?.resume_token || !resumeData.resume_endpoint) {
                console.error('[useAgentChat] Resume data missing on waiting form message')
                return
            }
            if (isLoadingRef.current) return

            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, isAwaitingInput: false, waitingForm: undefined }
                        : msg,
                ),
            )

            const agentMsgId = beginAgentMessage()
            const resumeToken = resumeData.resume_token
            const resumeEndpoint = `/api/crud${resumeData.resume_endpoint}`

            void (async () => {
                try {
                    const response = await fetch(resumeEndpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
                        credentials: 'include',
                        body: JSON.stringify({
                            resume_token: resumeToken,
                            answers: formValues,
                        }),
                    })
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                    }
                    try {
                        await response.text()
                    } catch {
                        /* drain body */
                    }

                    await runExecuteStream(
                        {
                            client_run_token: getClientToken(),
                            conversation_id: conversationIdRef.current,
                            resume_token: resumeToken,
                        },
                        agentMsgId,
                    )
                } catch (err) {
                    console.error('[useAgentChat] Resume form failed:', err)
                    finishStream(agentMsgId)
                }
            })()
        },
        [beginAgentMessage, runExecuteStream, finishStream],
    )

    const resumeAfterElicitation = useCallback(
        (messageId: string) => {
            const waitingMsg = messagesRef.current.find(m => m.id === messageId)
            const resumeToken = waitingMsg?.waitingForm?.resume_token
            if (!resumeToken || isLoadingRef.current) return

            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, isAwaitingInput: false, waitingForm: undefined }
                        : msg,
                ),
            )

            const agentMsgId = beginAgentMessage()
            void runExecuteStream(
                {
                    client_run_token: getClientToken(),
                    conversation_id: conversationIdRef.current,
                    resume_token: resumeToken,
                },
                agentMsgId,
            )
        },
        [beginAgentMessage, runExecuteStream],
    )

    const cancelWaitingForm = useCallback((messageId: string) => {
        setMessages(prev =>
            prev.map(msg =>
                msg.id === messageId
                    ? {
                          ...msg,
                          isAwaitingInput: false,
                          waitingForm: undefined,
                          content: 'Input cancelled.',
                      }
                    : msg,
            ),
        )
    }, [])

    const retryTool = useCallback(
        (_agentMessageId: string, tool: ToolExecution) => {
            const retryPrompt = `Please retry the tool "${tool.name}" with the same parameters.`
            sendMessage(retryPrompt, { displayMessage: `Retry ${tool.name.split('.').pop()}` })
        },
        [sendMessage],
    )

    const cancelRequest = useCallback(() => {
        stopElapsedInterval()
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        const stuckId = currentAgentMsgIdRef.current
        if (stuckId) {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === stuckId ? { ...msg, isStreaming: false } : msg,
                ),
            )
            currentAgentMsgIdRef.current = null
        }
        isLoadingRef.current = false
        setIsLoading(false)
        setConnectionStatus('connected')
        updateProgress(null)
    }, [stopElapsedInterval, updateProgress])

    const newChat = useCallback(() => {
        stopElapsedInterval()
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        const nextConversationId = generateConversationId()
        conversationIdRef.current = nextConversationId
        setConversationId(nextConversationId)
        currentAgentMsgIdRef.current = null
        isLoadingRef.current = false
        toolCallNamesRef.current = {}
        rateLimitAutoRetryCountRef.current = 0
        lastRequestRef.current = null
        setCurrentRunId(null)
        updateProgress(null)
        setMessages([])
        setIsLoading(false)
        setConnectionStatus('connected')
    }, [stopElapsedInterval, updateProgress])

    const restoreConversation = useCallback(
        (restoredConversationId: string, restoredMessages: ChatMessage[]) => {
            stopElapsedInterval()
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            conversationIdRef.current = restoredConversationId
            setConversationId(restoredConversationId)
            currentAgentMsgIdRef.current = null
            isLoadingRef.current = false
            setCurrentRunId(null)
            updateProgress(null)
            setMessages(
                restoredMessages.map(m => ({
                    ...m,
                    timestamp:
                        m.timestamp instanceof Date
                            ? m.timestamp
                            : new Date(m.timestamp as unknown as string),
                    isStreaming: false,
                })),
            )
            setIsLoading(false)
            setConnectionStatus('connected')
        },
        [stopElapsedInterval, updateProgress],
    )

    return {
        messages,
        connectionStatus,
        isLoading,
        configError: null,
        conversationId,
        currentRunId,
        currentProgress,
        sendMessage,
        cancelRequest,
        newChat,
        restoreConversation,
        resumeWaitingForm,
        resumeAfterElicitation,
        cancelWaitingForm,
        retryTool,
    }
}

export default useAgentChat
